import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * Reusable date input with month/year dropdowns.
 * Drop-in replacement for <input type="date"> - accepts ISO string values
 * and fires onChange with a synthetic event compatible with form handlers.
 *
 * Props:
 *   name        - field name (used in synthetic onChange event)
 *   value       - ISO date string (YYYY-MM-DD) or empty string
 *   onChange     - function(e) where e.target = { name, value }
 *   className   - CSS classes for the input
 *   max         - max date as ISO string
 *   min         - min date as ISO string
 *   openToDate  - Date object to open calendar at (useful for DOB)
 *   placeholder - placeholder text
 *   ...rest     - other props passed to DatePicker
 */

function parseDDMMYYYY(str) {
  const match = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

function formatDDMMYYYY(date) {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function toISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export default function DateInput({
  name,
  value,
  onChange,
  className,
  max,
  min,
  openToDate,
  placeholder = 'DD/MM/YYYY',
  onFocus,
  ...rest
}) {
  const [typedText, setTypedText] = useState(null);
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const maxDate = max ? new Date(max + 'T00:00:00') : undefined;
  const minDate = min ? new Date(min + 'T00:00:00') : undefined;

  const fireChange = (isoValue) => {
    onChange({ target: { name, value: isoValue } });
  };

  // Called when user clicks a day in the calendar
  const handleChange = (date) => {
    setTypedText(null);
    fireChange(date ? toISO(date) : '');
  };

  // Auto-format: insert slashes as user types digits (e.g. 01021990 -> 01/02/1990)
  const autoFormat = (raw) => {
    const digits = raw.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < digits.length && i < 8; i++) {
      if (i === 2 || i === 4) formatted += '/';
      formatted += digits[i];
    }
    return formatted;
  };

  // Called when user types in the input — prevent react-datepicker's
  // internal parsing (which mishandles dd/MM/yyyy partial input) and
  // only accept complete, valid DD/MM/YYYY dates.
  const handleChangeRaw = (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    const typed = e?.target?.value;
    if (typeof typed !== 'string') return;

    // Auto-format if the user is typing digits without slashes
    const formatted = autoFormat(typed);
    setTypedText(formatted);

    if (formatted === '') {
      fireChange('');
      return;
    }

    const parsed = parseDDMMYYYY(formatted);
    if (parsed) {
      fireChange(toISO(parsed));
    }
  };

  const handleBlur = () => {
    setTypedText(null);
  };

  // When typing, show the raw text; otherwise show formatted selected date
  const displayValue = typedText !== null
    ? typedText
    : selected
      ? formatDDMMYYYY(selected)
      : '';

  return (
    <DatePicker
      selected={selected}
      onChange={handleChange}
      onChangeRaw={handleChangeRaw}
      onBlur={handleBlur}
      value={displayValue}
      dateFormat="dd/MM/yyyy"
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      maxDate={maxDate}
      minDate={minDate}
      openToDate={openToDate || selected || undefined}
      placeholderText={placeholder}
      className={className}
      autoComplete="off"
      yearDropdownItemNumber={80}
      scrollableYearDropdown
      onFocus={onFocus}
      {...rest}
    />
  );
}
